"use client";

import { useEffect, useRef, useState, type FormEvent } from "react";
import {
  AlertCircle,
  ArrowRight,
  Briefcase,
  CheckCircle2,
  ChevronDown,
  Eye,
  EyeOff,
  FileText,
  MapPin,
  Paperclip,
  X,
} from "lucide-react";
import type { JobOfferItem, JobOffersApplyComponent } from "../../types";
import type { RendererConfig } from "../rendererConfig";
import { RuntimeImage } from "../../runtime/RendererRuntime";
import { resolveImageValue } from "../../utils/imageOptions";

/* Même intégration Turnstile que ContactFormRenderer (rendu explicite, un seul
   script pour toute la page). */
type TurnstileApi = {
  render: (
    container: HTMLElement,
    options: {
      sitekey: string;
      callback: (token: string) => void;
      "expired-callback"?: () => void;
      "error-callback"?: () => void;
    },
  ) => string;
  reset: (widgetId?: string) => void;
};

declare global {
  interface Window {
    turnstile?: TurnstileApi;
  }
}

const TURNSTILE_SCRIPT_SRC = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";

let turnstileLoader: Promise<TurnstileApi | null> | null = null;

function loadTurnstile(): Promise<TurnstileApi | null> {
  if (typeof window === "undefined") return Promise.resolve(null);
  if (window.turnstile) return Promise.resolve(window.turnstile);
  if (!turnstileLoader) {
    turnstileLoader = new Promise((resolve) => {
      const existing = document.querySelector<HTMLScriptElement>(`script[src="${TURNSTILE_SCRIPT_SRC}"]`);
      const script = existing ?? document.createElement("script");
      const settle = () => resolve(window.turnstile ?? null);
      script.addEventListener("load", settle);
      script.addEventListener("error", () => resolve(null));
      if (!existing) {
        script.src = TURNSTILE_SCRIPT_SRC;
        script.async = true;
        script.defer = true;
        document.head.appendChild(script);
      }
    });
  }
  return turnstileLoader;
}

type SubmitStatus = "idle" | "sending" | "success" | "error" | "turnstile-error" | "file-error";

const INPUT_CLASS =
  "w-full rounded-md border border-slate-300 bg-white px-4 py-2.5 text-sm xl:text-base text-[#0F2849] placeholder:text-slate-400 outline-none transition focus:border-[#0095DB] focus:ring-2 focus:ring-[#0095DB]/25";

const LABEL_CLASS = "mb-1.5 block text-sm xl:text-base font-semibold text-[#0F2849]";

/** Limite ANNONCÉE au candidat : 6 Mo par fichier. Le serveur tolère jusqu'à
 *  10 Mo (marge silencieuse pour les scans un peu lourds) — ne pas aligner les
 *  deux valeurs, la marge est voulue. */
const ANNOUNCED_MAX_FILE_BYTES = 6 * 1024 * 1024;
const MAX_FILES = 3;
/** Id du record « Autres » (candidature hors offres listées). Le panneau et l'option du
 *  formulaire n'apparaissent QUE si ce record offre-emploi est actif dans l'admin —
 *  l'activer/le désactiver dans Contenus est l'interrupteur. */
const OTHER_POSTE_ID = "autres";
const OTHER_POSTE_LABEL = "Autres";
/** Page formulaire par défaut ciblée par les boutons Postuler de la variante list. */
const DEFAULT_APPLY_PATH = "la-banque/carrieres/offres/postuler";

function isPdf(file: File): boolean {
  return file.type === "application/pdf" || /\.pdf$/i.test(file.name);
}

function formatMb(bytes: number): string {
  return `${(bytes / (1024 * 1024)).toFixed(1)} Mo`;
}

/**
 * Tableau des offres d'emploi + formulaire de candidature.
 *
 * Les offres arrivent résolues par l'hôte (content.mode="dynamic" + query sur la
 * collection `offre-emploi` → content.items) ; sans résolution (préversion admin)
 * les items inline restent affichés. Le formulaire poste en multipart vers
 * `/api/careers/apply` — une route du worker Cloudflare qui vérifie Turnstile
 * puis écrit la candidature dans le D1 + R2 dédiés : le serveur d'origine ne
 * reçoit JAMAIS les candidatures (surface anti-spam volontairement réduite).
 */
export function JobOffersApplyRenderer({
  component,
  config,
}: {
  component: JobOffersApplyComponent;
  config?: RendererConfig;
}) {
  const offers = (component.content.items ?? []).filter((o): o is JobOfferItem => Boolean(o && o.title));
  const intro = component.content.intro;
  const conditionsGenerales = component.content.conditionsGenerales ?? [];
  const turnstileSiteKey = config?.turnstileSiteKey;
  // "list" = cartes seules (Postuler → page formulaire) ; "form" = formulaire seul
  // (présélection via ?poste=) ; absent = les deux (compat pages existantes).
  const variant = component.content.variant;
  const showList = variant !== "form";
  const showForm = variant !== "list";
  const applyPath = (component.content.applyPath ?? DEFAULT_APPLY_PATH).replace(/^\/+/, "");
  const successImage = resolveImageValue(component.content.successImage);
  // Le record « Autres » (candidature spontanée) a son propre panneau, hors de la
  // grille des postes ; sa présence dans les items EST l'interrupteur admin.
  const otherOffer = offers.find((o) => o.id === OTHER_POSTE_ID) ?? null;
  const regularOffers = offers.filter((o) => o.id !== OTHER_POSTE_ID);

  const [selectedOffer, setSelectedOffer] = useState("");
  const [expanded, setExpanded] = useState<string | null>(null);
  const [prenom, setPrenom] = useState("");
  const [nom, setNom] = useState("");
  const [email, setEmail] = useState("");
  const [telephone, setTelephone] = useState("");
  const [motivation, setMotivation] = useState("");
  const [files, setFiles] = useState<File[]>([]);
  const [fileError, setFileError] = useState<string | null>(null);
  // Aperçu PDF par pièce jointe : une URL blob par fichier (créée à la demande,
  // révoquée au retrait/démontage) + la clé du fichier dont l'aperçu est ouvert.
  const previewUrls = useRef(new Map<File, string>());
  const [openPreview, setOpenPreview] = useState<string | null>(null);
  const canPreview = typeof URL !== "undefined" && typeof URL.createObjectURL === "function";
  const fileKey = (file: File) => `${file.name}-${file.size}`;
  const previewUrlFor = (file: File): string => {
    let url = previewUrls.current.get(file);
    if (!url) {
      url = URL.createObjectURL(file);
      previewUrls.current.set(file, url);
    }
    return url;
  };
  useEffect(() => {
    const urls = previewUrls.current;
    return () => {
      urls.forEach((u) => URL.revokeObjectURL?.(u));
      urls.clear();
    };
  }, []);
  // Honeypot — invisible pour un humain ; rempli = bot, on avale sans envoyer.
  const [website, setWebsite] = useState("");
  const [status, setStatus] = useState<SubmitStatus>("idle");
  const [serverMessage, setServerMessage] = useState<string | null>(null);

  const formRef = useRef<HTMLElement | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const turnstileContainerRef = useRef<HTMLDivElement | null>(null);
  const widgetIdRef = useRef<string | null>(null);
  const tokenRef = useRef<string>("");

  useEffect(() => {
    if (!turnstileSiteKey) return;
    let cancelled = false;
    loadTurnstile().then((api) => {
      if (cancelled || !api || !turnstileContainerRef.current || widgetIdRef.current !== null) return;
      widgetIdRef.current = api.render(turnstileContainerRef.current, {
        sitekey: turnstileSiteKey,
        callback: (token) => {
          tokenRef.current = token;
        },
        "expired-callback": () => {
          tokenRef.current = "";
        },
        "error-callback": () => {
          tokenRef.current = "";
        },
      });
    });
    return () => {
      cancelled = true;
    };
  }, [turnstileSiteKey]);

  // Présélection du poste depuis l'URL (?poste=<id>) — la page liste construit ces
  // liens ; seule une valeur qui correspond à une offre réellement listée est retenue.
  useEffect(() => {
    if (!showForm || typeof window === "undefined") return;
    const wanted = new URLSearchParams(window.location.search).get("poste");
    if (wanted && offers.some((o) => o.id === wanted)) {
      setSelectedOffer(wanted);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const resetTurnstile = () => {
    tokenRef.current = "";
    if (widgetIdRef.current !== null) {
      window.turnstile?.reset(widgetIdRef.current);
    }
  };

  const applyTo = (offer: JobOfferItem) => {
    setSelectedOffer(offer.id);
    formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  /** Lien vers la page formulaire, poste présélectionné (variante list). */
  const applyHrefFor = (offerId: string) => `/${applyPath}?poste=${encodeURIComponent(offerId)}`;

  /** Bouton Postuler d'une carte : lien vers la page formulaire en variante list,
   *  défilement vers le formulaire local sinon (page combinée). */
  const applyAction = (offer: JobOfferItem) =>
    variant === "list" ? (
      <a
        href={applyHrefFor(offer.id)}
        className="inline-flex items-center gap-2 rounded-full bg-[#005CA9] px-5 py-2 text-sm font-semibold text-white transition hover:bg-[#0F2849] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0095DB]/50 focus-visible:ring-offset-2"
      >
        Postuler <ArrowRight className="h-4 w-4" aria-hidden="true" />
      </a>
    ) : (
      <button
        type="button"
        onClick={() => applyTo(offer)}
        className="inline-flex items-center gap-2 rounded-full bg-[#005CA9] px-5 py-2 text-sm font-semibold text-white transition hover:bg-[#0F2849] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0095DB]/50 focus-visible:ring-offset-2"
      >
        Postuler <ArrowRight className="h-4 w-4" aria-hidden="true" />
      </button>
    );

  const addFiles = (incoming: FileList | null) => {
    if (!incoming || incoming.length === 0) return;
    setFileError(null);
    const next = [...files];
    for (const file of Array.from(incoming)) {
      if (next.length >= MAX_FILES) {
        setFileError(`Vous pouvez joindre au maximum ${MAX_FILES} documents.`);
        break;
      }
      if (!isPdf(file)) {
        setFileError(`« ${file.name} » n'est pas un PDF — seuls les documents PDF sont acceptés.`);
        continue;
      }
      if (file.size > ANNOUNCED_MAX_FILE_BYTES) {
        setFileError(`« ${file.name} » dépasse la taille maximale de 6 Mo (${formatMb(file.size)}).`);
        continue;
      }
      if (next.some((f) => f.name === file.name && f.size === file.size)) continue;
      next.push(file);
    }
    setFiles(next);
    // Autorise la re-sélection du même fichier après retrait.
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const removeFile = (index: number) => {
    const file = files[index];
    if (file) {
      const url = previewUrls.current.get(file);
      if (url) {
        URL.revokeObjectURL?.(url);
        previewUrls.current.delete(file);
      }
      if (openPreview === fileKey(file)) setOpenPreview(null);
    }
    setFiles((prev) => prev.filter((_, i) => i !== index));
    setFileError(null);
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (status === "sending") return;

    if (website.trim() !== "") {
      setStatus("success");
      return;
    }
    if (files.length === 0) {
      setFileError("Veuillez joindre au moins un document PDF (votre CV).");
      setStatus("file-error");
      return;
    }

    const offer = offers.find((o) => o.id === selectedOffer);
    setStatus("sending");
    setServerMessage(null);
    try {
      const form = new FormData();
      form.set("posteId", selectedOffer);
      form.set("posteTitre", offer?.title ?? (selectedOffer === OTHER_POSTE_ID ? OTHER_POSTE_LABEL : ""));
      form.set("domaine", offer?.domaine ?? "");
      form.set("prenom", prenom);
      form.set("nom", nom);
      form.set("email", email);
      form.set("telephone", telephone);
      form.set("motivation", motivation);
      if (tokenRef.current) form.set("turnstileToken", tokenRef.current);
      files.forEach((file) => form.append("documents", file, file.name));

      const response = await fetch("/api/careers/apply", { method: "POST", body: form });
      if (response.ok) {
        setStatus("success");
        return;
      }
      const payload = (await response.json().catch(() => null)) as { error?: string; message?: string } | null;
      resetTurnstile();
      setServerMessage(payload?.message ?? null);
      setStatus(payload?.error === "turnstile" ? "turnstile-error" : "error");
    } catch {
      resetTurnstile();
      setStatus("error");
    }
  };

  return (
    <div className="space-y-8">
      {/* ===== Liste des postes disponibles ===== */}
      {showList ? (
      <section id="postes-disponibles" aria-label="Postes disponibles" className="scroll-mt-28">
        {intro ? <p className="mb-6 text-sm xl:text-base leading-relaxed text-[#0F2849]">{intro}</p> : null}
        {regularOffers.length === 0 ? (
          <div className="rounded-md bg-white p-6 ring-1 ring-slate-200">
            <p className="text-sm xl:text-base text-[#0F2849]">
              Aucun poste n'est ouvert pour le moment. Les prochaines offres seront publiées sur cette page.
            </p>
          </div>
        ) : (
          <ul className="grid gap-5 md:grid-cols-2 xl:grid-cols-3">
            {regularOffers.map((offer) => {
              const isOpen = expanded === offer.id;
              return (
                <li
                  key={offer.id}
                  className="group flex h-full flex-col rounded-lg bg-white p-5 shadow-sm ring-1 ring-slate-200 transition duration-200 hover:-translate-y-0.5 hover:shadow-lg hover:ring-[#0095DB]/40 sm:p-6"
                >
                  <div className="flex items-start gap-3.5">
                    <span className="grid h-11 w-11 shrink-0 place-items-center rounded-lg bg-gradient-to-br from-[#0069B4] to-[#005CA9] text-white shadow-sm">
                      <Briefcase className="h-5 w-5" aria-hidden="true" />
                    </span>
                    <h3 className="min-w-0 text-base font-semibold leading-snug text-[#0F2849] transition group-hover:text-[#005CA9] xl:text-lg">
                      {offer.title}
                    </h3>
                  </div>
                  <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1.5">
                    {offer.domaine ? (
                      <span className="inline-flex items-center rounded-full bg-[#F4F8FC] px-2.5 py-0.5 text-[12px] font-semibold text-[#005CA9] ring-1 ring-[#005CA9]/15">
                        {offer.domaine}
                      </span>
                    ) : null}
                    {offer.lieu ? (
                      <span className="inline-flex items-center gap-1 text-[12px] font-medium text-slate-500">
                        <MapPin className="h-3.5 w-3.5 text-[#0095DB]" aria-hidden="true" />
                        {offer.lieu}
                      </span>
                    ) : null}
                  </div>
                  {offer.description ? (
                    /* flex-1 : la zone description absorbe la hauteur libre, donc les
                       pieds de carte restent alignés sur toute la rangée. */
                    <p className={`mt-3 flex-1 text-sm leading-relaxed text-[#0F2849]/85 xl:text-[15px] ${isOpen ? "" : "line-clamp-3"}`}>
                      {offer.description}
                    </p>
                  ) : (
                    <div className="flex-1" aria-hidden="true" />
                  )}
                  {isOpen && offer.conditions ? (
                    <p className="mt-2 text-sm leading-relaxed text-slate-600">{offer.conditions}</p>
                  ) : null}
                  <div className="mt-4 flex items-center justify-between gap-3 border-t border-[#005CA9]/10 pt-4">
                    {offer.description || offer.conditions ? (
                      <button
                        type="button"
                        onClick={() => setExpanded(isOpen ? null : offer.id)}
                        aria-expanded={isOpen}
                        className="inline-flex items-center gap-1 whitespace-nowrap text-sm font-semibold text-[#005CA9] transition hover:text-[#0F2849]"
                      >
                        {isOpen ? "Réduire" : "Voir le détail"}
                        <ChevronDown className={`h-4 w-4 transition-transform ${isOpen ? "rotate-180" : ""}`} aria-hidden="true" />
                      </button>
                    ) : (
                      <span />
                    )}
                    {applyAction(offer)}
                  </div>
                </li>
              );
            })}
          </ul>
        )}

        {/* ===== Panneau « Autres » (candidature spontanée) — présent SEULEMENT si le
                record offre-emploi « autres » est actif dans l'admin ===== */}
        {otherOffer ? (
          <div className="mt-6 flex flex-col gap-4 rounded-md border border-dashed border-[#005CA9]/35 bg-[#F4F8FC] p-5 sm:flex-row sm:items-center sm:justify-between sm:p-6">
            <div className="flex items-start gap-3">
              <span className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-white text-[#005CA9] ring-1 ring-slate-200">
                <Briefcase className="h-[18px] w-[18px]" aria-hidden="true" />
              </span>
              <div className="min-w-0">
                <h3 className="text-base xl:text-lg font-semibold leading-snug text-[#005CA9]">{otherOffer.title}</h3>
                {otherOffer.description ? (
                  <p className="mt-1.5 text-sm xl:text-[15px] leading-relaxed text-[#0F2849]">{otherOffer.description}</p>
                ) : null}
              </div>
            </div>
            <div className="shrink-0 sm:pl-4">{applyAction(otherOffer)}</div>
          </div>
        ) : null}
      </section>
      ) : null}

      {/* ===== Formulaire de candidature ===== */}
      {showForm ? (
      <section
        ref={formRef}
        id="careers-form"
        aria-label="Formulaire de candidature"
        className="relative overflow-hidden rounded-md bg-white p-6 ring-1 ring-slate-200 sm:p-8 scroll-mt-28"
      >
        {variant === "form" ? null : (
          /* La page formulaire dédiée porte déjà ce titre dans son PageHeader. */
          <>
            <h2 className="text-lg xl:text-xl font-semibold text-[#005CA9]">Déposer ma candidature</h2>
            <div className="mt-2 h-1 w-12 rounded-sm bg-[#FFA401]" aria-hidden="true" />
          </>
        )}
        {conditionsGenerales.length > 0 && status !== "success" ? (
          <ul className="mt-4 space-y-1.5 text-sm xl:text-[15px] leading-relaxed text-[#0F2849]">
            {conditionsGenerales.map((cond) => (
              <li key={cond} className="flex items-start gap-2">
                <span aria-hidden="true" className="mt-[7px] h-1.5 w-1.5 shrink-0 rounded-full bg-[#FFA401]" />
                {cond}
              </li>
            ))}
          </ul>
        ) : null}

        {status === "success" && successImage.src ? (
          /* Écran « formulaire envoyé » du designer : fond flouté bleu nuit,
             texte centré serif, bouton ambre de retour. */
          <div role="status" aria-live="polite" className="relative -m-6 overflow-hidden sm:-m-8">
            <RuntimeImage
              src={successImage.src}
              alt=""
              aria-hidden="true"
              fill
              sizes="(max-width: 1200px) 100vw, 1200px"
              className="absolute inset-0 h-full w-full scale-110 object-cover object-center blur-[6px]"
            />
            <div className="absolute inset-0 bg-[#001d45]/70" aria-hidden="true" />
            <div className="relative z-10 flex flex-col items-center gap-5 px-6 py-16 text-center text-white sm:py-24">
              <p className="text-xl font-bold leading-snug sm:text-2xl" style={{ fontFamily: "var(--font-aleo)" }}>
                Votre formulaire a été envoyé avec succès !
                <br />
                La BCC vous remercie pour votre intérêt.
              </p>
              <p className="max-w-md text-sm leading-relaxed text-white/85 sm:text-base">
                Nous vous invitons à revenir régulièrement sur notre site pour consulter les nouvelles offres
                d'emploi.
              </p>
              <a
                href="/"
                className="mt-2 inline-flex items-center gap-2 rounded-full bg-[#FFA401] px-6 py-2.5 text-sm font-semibold text-white transition hover:brightness-110 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/70 sm:text-base"
              >
                Revenir sur le site
                <ArrowRight className="h-4 w-4" aria-hidden="true" />
              </a>
            </div>
          </div>
        ) : status === "success" ? (
          <div role="status" aria-live="polite" className="flex flex-col items-center gap-5 py-10 text-center sm:py-14">
            <span className="grid h-20 w-20 place-items-center rounded-full bg-[#0095DB]/10 ring-8 ring-[#0095DB]/5">
              <CheckCircle2 className="h-10 w-10 text-[#005CA9]" aria-hidden="true" />
            </span>
            <div>
              <p className="text-xl font-bold text-[#005CA9] sm:text-2xl" style={{ fontFamily: "var(--font-aleo)" }}>
                Merci pour votre candidature !
              </p>
              <p className="mx-auto mt-2 max-w-md text-sm leading-relaxed text-[#0F2849] xl:text-base">
                Votre dossier a bien été transmis à la Banque Centrale du Congo et sera examiné avec
                toute l'attention qu'il mérite.
              </p>
              <p className="mx-auto mt-1.5 max-w-md text-sm leading-relaxed text-slate-500">
                N'hésitez pas à revenir consulter régulièrement les nouvelles offres d'emploi.
              </p>
            </div>
            <a
              href={`/${DEFAULT_APPLY_PATH.replace(/\/postuler$/, "")}`}
              className="mt-1 inline-flex items-center gap-2 rounded-full bg-[#005CA9] px-6 py-2.5 text-sm font-semibold text-white transition hover:bg-[#0F2849] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0095DB]/50 focus-visible:ring-offset-2 sm:text-base"
            >
              Voir les autres offres
              <ArrowRight className="h-4 w-4" aria-hidden="true" />
            </a>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="mt-6 space-y-5">
            <div>
              <label htmlFor="careers-poste" className={LABEL_CLASS}>
                Poste visé <span className="text-rose-600">*</span>
              </label>
              <div className="relative">
                <select
                  id="careers-poste"
                  name="poste"
                  required
                  value={selectedOffer}
                  onChange={(e) => setSelectedOffer(e.target.value)}
                  className={`${INPUT_CLASS} appearance-none pr-10`}
                >
                  <option value="" disabled>
                    Choisir un poste
                  </option>
                  {regularOffers.map((offer) => (
                    <option key={offer.id} value={offer.id}>
                      {offer.title}
                    </option>
                  ))}
                  {otherOffer ? (
                    <option value={otherOffer.id}>{otherOffer.title || OTHER_POSTE_LABEL}</option>
                  ) : null}
                </select>
                <ChevronDown
                  className="pointer-events-none absolute right-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-[#005CA9]"
                  aria-hidden="true"
                />
              </div>
            </div>
            <div className="grid gap-5 sm:grid-cols-2">
              <div>
                <label htmlFor="careers-prenom" className={LABEL_CLASS}>
                  Prénom <span className="text-rose-600">*</span>
                </label>
                <input
                  id="careers-prenom"
                  name="prenom"
                  required
                  maxLength={100}
                  value={prenom}
                  onChange={(e) => setPrenom(e.target.value)}
                  placeholder="Prénom"
                  className={INPUT_CLASS}
                />
              </div>
              <div>
                <label htmlFor="careers-nom" className={LABEL_CLASS}>
                  Nom <span className="text-rose-600">*</span>
                </label>
                <input
                  id="careers-nom"
                  name="nom"
                  required
                  maxLength={100}
                  value={nom}
                  onChange={(e) => setNom(e.target.value)}
                  placeholder="Nom"
                  className={INPUT_CLASS}
                />
              </div>
            </div>
            <div className="grid gap-5 sm:grid-cols-2">
              <div>
                <label htmlFor="careers-email" className={LABEL_CLASS}>
                  Adresse e-mail <span className="text-rose-600">*</span>
                </label>
                <input
                  id="careers-email"
                  type="email"
                  name="email"
                  required
                  maxLength={320}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="nom@exemple.cd"
                  className={INPUT_CLASS}
                />
              </div>
              <div>
                <label htmlFor="careers-telephone" className={LABEL_CLASS}>
                  Téléphone <span className="text-rose-600">*</span>
                </label>
                <input
                  id="careers-telephone"
                  type="tel"
                  name="telephone"
                  required
                  maxLength={30}
                  value={telephone}
                  onChange={(e) => setTelephone(e.target.value)}
                  placeholder="+243 ..."
                  className={INPUT_CLASS}
                />
              </div>
            </div>
            <div>
              <label htmlFor="careers-motivation" className={LABEL_CLASS}>
                Motivation <span className="text-rose-600">*</span>
              </label>
              <textarea
                id="careers-motivation"
                name="motivation"
                rows={6}
                required
                maxLength={5000}
                value={motivation}
                onChange={(e) => setMotivation(e.target.value)}
                placeholder="Présentez brièvement votre parcours et votre motivation"
                className={`${INPUT_CLASS} resize-y py-3`}
              />
            </div>
            <div>
              <span className={LABEL_CLASS}>
                Documents (CV, lettre, diplôme) <span className="text-rose-600">*</span>
              </span>
              <p id="careers-files-help" className="mb-2 text-xs xl:text-sm text-slate-500">
                1 à {MAX_FILES} fichiers PDF, 6 Mo maximum par fichier.
              </p>
              <label
                htmlFor="careers-documents"
                className="flex cursor-pointer items-center gap-2.5 rounded-md border border-dashed border-slate-300 bg-[#F4F8FC] px-4 py-3.5 text-sm xl:text-base font-medium text-[#005CA9] transition hover:border-[#0095DB] hover:bg-[#EAF2F9]"
              >
                <Paperclip className="h-[18px] w-[18px]" aria-hidden="true" />
                Ajouter un document PDF
              </label>
              <input
                ref={fileInputRef}
                id="careers-documents"
                name="documents"
                type="file"
                accept="application/pdf,.pdf"
                multiple
                onChange={(e) => addFiles(e.target.files)}
                aria-describedby="careers-files-help"
                className="sr-only"
              />
              {files.length > 0 ? (
                <ul className="mt-3 space-y-2">
                  {files.map((file, index) => {
                    const key = fileKey(file);
                    const isPreviewOpen = openPreview === key;
                    return (
                      <li key={key} className="rounded-md bg-[#F4F8FC] px-3.5 py-2.5 ring-1 ring-slate-200">
                        <div className="flex items-center justify-between gap-3">
                          <span className="flex min-w-0 items-center gap-2 text-sm text-[#0F2849]">
                            <FileText className="h-4 w-4 shrink-0 text-[#005CA9]" aria-hidden="true" />
                            <span className="truncate">{file.name}</span>
                            <span className="shrink-0 text-xs text-slate-500">{formatMb(file.size)}</span>
                          </span>
                          <span className="flex shrink-0 items-center gap-1.5">
                            {canPreview ? (
                              <button
                                type="button"
                                onClick={() => setOpenPreview(isPreviewOpen ? null : key)}
                                aria-expanded={isPreviewOpen}
                                aria-label={isPreviewOpen ? `Fermer l'aperçu de ${file.name}` : `Aperçu de ${file.name}`}
                                className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold text-[#005CA9] transition hover:bg-white"
                              >
                                {isPreviewOpen ? (
                                  <EyeOff className="h-4 w-4" aria-hidden="true" />
                                ) : (
                                  <Eye className="h-4 w-4" aria-hidden="true" />
                                )}
                                Aperçu
                              </button>
                            ) : null}
                            <button
                              type="button"
                              onClick={() => removeFile(index)}
                              aria-label={`Retirer ${file.name}`}
                              className="grid h-7 w-7 place-items-center rounded-full text-slate-500 transition hover:bg-white hover:text-rose-600"
                            >
                              <X className="h-4 w-4" aria-hidden="true" />
                            </button>
                          </span>
                        </div>
                        {isPreviewOpen ? (
                          <iframe
                            title={`Aperçu de ${file.name}`}
                            src={`${previewUrlFor(file)}#toolbar=0&navpanes=0&view=FitH`}
                            className="mt-2.5 h-64 w-full rounded-md bg-white ring-1 ring-slate-200"
                          />
                        ) : null}
                      </li>
                    );
                  })}
                </ul>
              ) : null}
              {fileError ? (
                <p role="alert" className="mt-2 flex items-start gap-2 text-sm font-medium text-rose-700">
                  <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
                  {fileError}
                </p>
              ) : null}
            </div>
            {/* Honeypot : champ invisible pour un humain, rempli par les bots. */}
            <div className="absolute -left-[9999px] top-auto h-px w-px overflow-hidden" aria-hidden="true">
              <label htmlFor="careers-website">Site web</label>
              <input
                id="careers-website"
                name="website"
                type="text"
                tabIndex={-1}
                autoComplete="off"
                value={website}
                onChange={(e) => setWebsite(e.target.value)}
              />
            </div>
            {turnstileSiteKey ? <div ref={turnstileContainerRef} data-testid="turnstile-container" className="pt-1" /> : null}
            <div aria-live="polite" className="space-y-3">
              {status === "error" ? (
                <p
                  role="alert"
                  className="flex items-start gap-2.5 rounded-md border border-rose-200 bg-rose-50 px-4 py-3 text-sm xl:text-base font-medium text-rose-700"
                >
                  <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
                  {serverMessage ?? "Une erreur est survenue, veuillez réessayer."}
                </p>
              ) : null}
              {status === "turnstile-error" ? (
                <p
                  role="alert"
                  className="flex items-start gap-2.5 rounded-md border border-rose-200 bg-rose-50 px-4 py-3 text-sm xl:text-base font-medium text-rose-700"
                >
                  <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
                  La vérification anti-spam a échoué, veuillez réessayer.
                </p>
              ) : null}
            </div>
            <div className="flex flex-col gap-3 border-t border-[#005CA9]/10 pt-5 sm:flex-row sm:items-center sm:justify-between">
              <span className="text-xs xl:text-sm text-slate-500">
                <span className="text-rose-600">*</span> Mentions obligatoires
              </span>
              <button
                type="submit"
                disabled={status === "sending"}
                className="inline-flex items-center justify-center gap-2 self-start rounded-full bg-[#005CA9] px-6 py-2.5 text-sm xl:text-base font-semibold text-white transition hover:bg-[#0F2849] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0095DB]/50 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60 sm:self-auto"
              >
                {status === "sending" ? "Envoi en cours…" : "J'envoie ma candidature"}
                <ArrowRight className="h-4 w-4" aria-hidden="true" />
              </button>
            </div>
          </form>
        )}
      </section>
      ) : null}
    </div>
  );
}
